const INTERVAL_FORM_OPTIONS = [
  { formValue: '1', key: '5m', label: '5 minute' },
  { formValue: '2', key: '1h', label: '1 hour' },
  { formValue: '3', key: '1d', label: 'Everyday' },
];

const AGGREGATION_FORM_OPTIONS = [
  { formValue: '3', key: 'portfolio', label: 'Portfolio' },
  { formValue: '4', key: 'device', label: 'Power Block' },
  { formValue: '1', key: 'individual', label: 'Individual' },
  { formValue: '2', key: 'device_group', label: 'Sector' },
];

const AGGREGATE_FORM_OPTIONS = [
  { formValue: '1', fn: 'AVG', label: 'AVG' },
  { formValue: '2', fn: 'MIN', label: 'MIN' },
  { formValue: '3', fn: 'MAX', label: 'MAX' },
  { formValue: '4', fn: 'SUM', label: 'SUM' },
  { formValue: '5', fn: 'COUNT', label: 'COUNT' },
  { formValue: '6', fn: 'LAST', label: 'LAST' },
];

function isSectorTemplate(template) {
  if (!template) {
    return false;
  }
  return (
    template.id === 'mon-sector-xfmr'
    || template.graphKey === 'sector-metrics'
  );
}

/**
 * Aggregation Type options from DIM_EXPORT_TEMPLATE.supported_aggregations.
 * Falls back to legacy deviceScoped / sector / portfolio heuristics when unset.
 */
function aggregationOptionsForTemplate(template) {
  if (!template) {
    return [];
  }

  const supported = Array.isArray(template.supportedAggregations)
    ? template.supportedAggregations
        .map((token) => String(token).trim().toLowerCase())
        .filter(Boolean)
    : [];

  if (supported.length > 0) {
    const byKey = new Map(AGGREGATION_FORM_OPTIONS.map((option) => [option.key, option]));
    return supported
      .map((key) => byKey.get(key))
      .filter(Boolean);
  }

  if (template.deviceScoped) {
    return AGGREGATION_FORM_OPTIONS.filter((option) => option.key === 'individual');
  }
  if (isSectorTemplate(template)) {
    return AGGREGATION_FORM_OPTIONS.filter((option) => option.key === 'device_group');
  }
  return AGGREGATION_FORM_OPTIONS.filter((option) => option.key === 'portfolio');
}

function intervalOptionsForTemplate(template) {
  const supported = new Set(template?.supportedIntervals ?? ['5m', '1h', '1d']);
  return INTERVAL_FORM_OPTIONS.filter((option) => supported.has(option.key));
}

function defaultIntervalFormValue(template) {
  const options = intervalOptionsForTemplate(template);
  const match = options.find((option) => option.key === template?.defaultInterval);
  return match?.formValue ?? options[0]?.formValue ?? '1';
}

function defaultAggregationFormValue(template) {
  const options = aggregationOptionsForTemplate(template);
  const preferred = template?.defaultAggregation ?? options[0]?.key;
  const match = options.find((option) => option.key === preferred);
  return match?.formValue ?? options[0]?.formValue ?? '3';
}

function aggregateOptionsForMetric(metric) {
  const allowed = new Set(
    (metric?.allowedAggregations ?? []).map((value) => String(value).toUpperCase()),
  );
  if (allowed.size === 0) {
    return AGGREGATE_FORM_OPTIONS;
  }
  return AGGREGATE_FORM_OPTIONS.filter((option) => allowed.has(option.fn));
}

function defaultAggregateFormValue(metric) {
  const options = aggregateOptionsForMetric(metric);
  const unit = String(metric?.unit ?? '').toUpperCase();
  if (unit === 'MWH') {
    const sum = options.find((option) => option.fn === 'SUM');
    if (sum) {
      return sum.formValue;
    }
  }
  const firstAllowed = String(metric?.allowedAggregations?.[0] ?? '').toUpperCase();
  if (firstAllowed) {
    const match = options.find((option) => option.fn === firstAllowed);
    if (match) {
      return match.formValue;
    }
  }
  if (unit === 'MW') {
    const max = options.find((option) => option.fn === 'MAX');
    if (max) {
      return max.formValue;
    }
  }
  return options[0]?.formValue ?? '1';
}

export function getExportTemplateFormConfig(template) {
  if (!template) {
    return {
      hasTemplate: false,
      showInterval: false,
      showAggregation: false,
      showDeviceId: false,
      showSector: false,
      usesPowerblockDeviceList: false,
      deviceIdLabel: 'Device ID',
      showCategory: false,
      showFilters: false,
      showDataPoints: false,
      intervalOptions: [],
      aggregationOptions: [],
      metrics: [],
      defaultInterval: '1',
      defaultAggregation: '3',
      fieldRequirements: {
        template: true,
        startDate: false,
        endDate: false,
        interval: false,
        aggregation: false,
        deviceId: false,
        sector: false,
        category: false,
        filters: false,
        dataPoints: false,
      },
    };
  }

  const metrics = Array.isArray(template.metrics) ? template.metrics : [];
  const intervalOptions = intervalOptionsForTemplate(template);
  const aggregationOptions = aggregationOptionsForTemplate(template);

  return {
    hasTemplate: true,
    showInterval: intervalOptions.length > 0,
    showAggregation: aggregationOptions.length > 0,
    showDeviceId: false,
    showSector: isSectorTemplate(template),
    usesPowerblockDeviceList: false,
    deviceIdLabel: 'Device ID',
    showCategory: Boolean(template.categoryDefault),
    showFilters: metrics.length > 0,
    showDataPoints: metrics.length > 0,
    intervalOptions,
    aggregationOptions,
    metrics,
    defaultInterval: defaultIntervalFormValue(template),
    defaultAggregation: defaultAggregationFormValue(template),
    fieldRequirements: {
      template: true,
      startDate: true,
      endDate: true,
      interval: intervalOptions.length > 1,
      aggregation: aggregationOptions.length > 1,
      deviceId: false,
      sector: isSectorTemplate(template),
      category: false,
      filters: false,
      dataPoints: false,
    },
  };
}

export {
  aggregateOptionsForMetric,
  defaultAggregateFormValue,
};
